// src/components/forms/OrderForm.tsx
// Formulario de pedidos refactorizado usando hooks y componentes modulares

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import OrderItemsTablePedido from '../tables/OrderItemsTablePedido'
import type { OrderFormProps } from '../../types/orderFormTypes'
import {
  renderDepartamentoOptions as renderDepartamentoOptionsUtil,
  renderCiudadOptions as renderCiudadOptionsUtil,
  getOrderTotalCopAmount
} from '../../utils/orderFormUtils'
import {
  applyClienteNetoToggle,
  isClienteNetoActive
} from '../../utils/clienteNetoOrder'
import {
  canEditOrderProductsForUser,
  isOrderFormRestrictedForUser
} from '../../utils/orderFormEditRules'
import { usePagosParciales } from '../../hooks/usePagosParciales'
import { Button, Switch } from '../ui'
import SectionTitle from '../ui/SectionTitle'
import { XMarkIcon, CheckIcon, CubeIcon } from '@heroicons/react/24/outline'
import { useCotizaciones } from '../../hooks/useCotizaciones'
import { CotizacionesModal } from '../modals/CotizacionesModal'
import {
  buildOrderQuoteRequest,
  canQuoteOrderShipment,
  hasCalculatedPackageDimensions,
  mapQuoteCarrierToTransportadora,
  packageDimensionsForOrderLines,
  type ManualPackageDimensions
} from '../../utils/orderQuoteFromOrder'
import { OrderQuoteDimensionsModal } from './order/OrderQuoteDimensionsModal'

// Hooks
import { useOrderForm, useOrderProducts, useOrderValidation, useOrderModals, useOrderPaymentSync, useRecogeEnBodega, useOrderInitialization, useOrderHandlers, useOrderSubmit } from '../../hooks/order'

// Componentes compartidos
import { CustomerInfoSection, AddressSection, ProductSearchInput } from './shared'

// Componentes específicos
import { OrderGeneralInfo, OrderPaymentInfo, OrderShippingInfo, OrderActions, OrderBodegaEntregaSection } from './order'

// Re-exportar OrderData para mantener compatibilidad
export type { OrderData } from '../../types/orderFormTypes'

const OrderForm: React.FC<OrderFormProps> = (props) => {
  const { 
    initialData, 
    mode, 
    onSubmit, 
    onFormChange,
    tiposDeDocumento: tiposDoc = [],
    departamentos: deptos = [],
    ciudades: cities = [],
    formaPago: formasPago = [],
    metodoPago: metodosPago = [],
    plazosPago = [],
    productStockRefreshKey = 0
  } = props

  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const [costoEnvio, setCostoEnvio] = useState(initialData?.envio || initialData?.extraDetails?.costo_envio || '0')
  const [showPackageDimensionsModal, setShowPackageDimensionsModal] = useState(false)
  const {
    cotizaciones,
    todasLasCotizaciones,
    showModal: showCotizacionesModal,
    cotizar,
    cerrarModal
  } = useCotizaciones()

  // Hook para manejar modales y estado de submit
  const {
    modals,
    isSubmitting,
    setIsSubmitting,
    setShowNoteModal,
    setShowAuditModal,
    setShowTrackingModal,
    setShowShippingLabelModal,
    setShowInvoiceUploadModal
  } = useOrderModals()

  // Hook de formulario
  const {
    formData,
    setFormData,
    handleInputChange
  } = useOrderForm({
    initialData,
    mode,
    departamentos: deptos,
    ciudades: cities,
    onFormChange
  })

  // Hook para bodega / recoge / fecha entrega
  const {
    eligeBodega,
    recogeEnBodega,
    recogeRespuesta,
    handleRecogeRespuestaChange,
    bodegaRecogidaSeleccion,
    handleBodegaRecogidaSeleccionChange,
    fechaEntregaActiva,
    handleFechaEntregaActivaChange,
    fechaEntrega,
    handleFechaEntregaChange
  } = useRecogeEnBodega({
    initialData,
    mode,
    setFormData
  })

  const usaSaldoBodegaSeleccionada = eligeBodega && bodegaRecogidaSeleccion !== ''

  // Hook para inicializar datos antiguos del pedido
  const { oldDataOrder } = useOrderInitialization({
    initialData,
    mode
  })

  // Hook de productos
  const {
    searchTerm,
    searchResults,
    isSearching,
    showResults,
    setShowResults,
    handleSearchChange,
    productDetails,
    expandedCombos,
    loadingDetails,
    handleSelectProduct,
    handleQuantityChange,
    handleDiscountChange,
    handlePriceChange,
    handleRemoveProduct,
    toggleCombo,
    calculateTotals,
    markLineItemsCnApplied,
    reloadProductDetails
  } = useOrderProducts({
    formData,
    setFormData,
    mode,
    costoEnvio,
    recogeEnBodega: usaSaldoBodegaSeleccionada,
    bodegaRecogidaSeleccion
  })

  const lastStockRefreshKeyRef = useRef(0)
  useEffect(() => {
    if (!productStockRefreshKey || productStockRefreshKey === lastStockRefreshKeyRef.current) {
      return
    }
    lastStockRefreshKeyRef.current = productStockRefreshKey
    void reloadProductDetails()
  }, [productStockRefreshKey, reloadProductDetails])

  const handleClienteNetoChange = useCallback((checked: boolean) => {
    let linesToMark: typeof formData.lineItems = []
    setFormData((prev) => {
      const result = applyClienteNetoToggle(prev, checked, productDetails, costoEnvio)
      if (!result.changed) {
        return prev
      }
      if (checked) {
        linesToMark = result.lineItems
      }
      return {
        ...prev,
        lineItems: result.lineItems,
        extraDetails: result.extraDetails,
        total: result.total
      }
    })
    if (linesToMark?.length) {
      markLineItemsCnApplied(linesToMark)
    }
  }, [setFormData, productDetails, costoEnvio, markLineItemsCnApplied])

  // Inicializar pagos parciales desde initialData
  const initialPagos = useMemo(() => {
    if (initialData?.extraDetails?.pagos && Array.isArray(initialData.extraDetails.pagos)) {
      return initialData.extraDetails.pagos.map((pago: any) => ({
        metodo_pago: pago.metodo_pago || '',
        valor: typeof pago.valor === 'string' ? Math.round(parseFloat(pago.valor)) : Math.round(pago.valor),
        id_transaccion: pago.id_transaccion || '',
        dcto: pago.dcto ? String(pago.dcto) : '',
        forma_pago: pago.forma_pago || initialData.extraDetails?.forma_pago || '6',
        plazo: pago.plazo || 30,
        pago_verificado: Number(pago.pago_verificado) === 1 ? 1 : 0,
        fecha_creacion: pago.fecha_creacion || new Date().toISOString()
      }))
    }
    return []
  }, [initialData])

  // Hook para pagos parciales (debe ir antes de useOrderValidation)
  const {
    pagosParciales,
    agregarPagoParcial,
    eliminarPagoParcial,
    actualizarPagoParcial: actualizarPagoParcialHook,
    valoresPagosParciales,
    esValido: isPagosParcialesValidHook
  } = usePagosParciales({
    formData,
    productDetails,
    costoEnvio,
    initialPagos
  })

  const { totalPedidoPagosParciales, sumaPagosParciales, esValidoPagosParciales } = valoresPagosParciales

  const getOrderTotalCop = useCallback(() => {
    return getOrderTotalCopAmount(
      formData.lineItems,
      productDetails,
      costoEnvio,
      formData.extraDetails
    )
  }, [formData.lineItems, formData.extraDetails, productDetails, costoEnvio])

  // Hook de validación (con pagos parciales) - DEBE ir después de pagosParciales
  const {
    errors,
    setErrors,
    validateForm,
    isStockAvailable,
    isBoldPaymentAmountValid,
    getValidationSummary
  } = useOrderValidation({
    formData,
    mode,
    recogeEnBodega,
    recogeRespuesta,
    usaSaldoBodegaSeleccionada,
    bodegaRecogidaSeleccion,
    fechaEntregaActiva,
    fechaEntrega,
    pagosParciales,
    productDetails,
    costoEnvio
  })

  // Wrapper para actualizarPagoParcial con tipo compatible
  const actualizarPagoParcial = useCallback((index: number, field: string, value: any) => {
    actualizarPagoParcialHook(index, field as any, value)
  }, [actualizarPagoParcialHook])

  // Hook para sincronizar forma de pago con método de pago
  useOrderPaymentSync({
    formData,
    mode,
    pagosParciales,
    handleInputChange,
    agregarPagoParcial,
    userRoleId: user.role_id
  })

  // Validación de email
  const handleEmailValidation = useCallback((email: string) => {
    if (email.toLowerCase().endsWith('.con')) {
      setErrors(prev => ({
        ...prev,
        email: 'El dominio .con no es válido. Por favor, use un dominio válido como .com, .co, etc.'
      }))
      } else {
      setErrors(prev => ({
        ...prev,
        email: ''
      }))
      }
  }, [setErrors])

  // Hook para handlers simplificados
  const {
    handleCustomerFirstNameChange,
    handleCustomerLastNameChange,
    handleCustomerEmailChange,
    handleCustomerPhoneChange,
    handleCustomerTipoDocumentoChange,
    handleCustomerDocumentoChange,
    handleBillingAddressChange,
    handleShippingAddressChange,
    handleNoteChange
  } = useOrderHandlers({
    handleInputChange,
    handleEmailValidation
  })

  // Hook para manejar el submit
  const { handleSubmit } = useOrderSubmit({
    formData,
    productDetails,
    costoEnvio,
    pagosParciales,
    eligeBodega,
    recogeEnBodega,
    bodegaRecogidaSeleccion,
    fechaEntregaActiva,
    fechaEntrega,
    mode,
    oldDataOrder,
    validateForm,
    getValidationSummary,
    calculateTotals,
    setIsSubmitting,
    onSubmit,
    userRoleId: user.role_id
  })

  // Funciones de renderizado de opciones
  const renderDepartamentoOptions = useCallback((addressType: 'billing' | 'shipping') => {
    return renderDepartamentoOptionsUtil(addressType, formData, deptos)
  }, [formData, deptos])

  const renderCiudadOptions = useCallback((addressType: 'billing' | 'shipping') => {
    return renderCiudadOptionsUtil(addressType, formData, cities)
  }, [formData, cities])

  const isPagosParcialesValid = useCallback(() => {
    return isPagosParcialesValidHook()
  }, [isPagosParcialesValidHook])

  const isFormRestricted = isOrderFormRestrictedForUser(mode, formData.status, user.role_id)
  const canEditProducts = canEditOrderProductsForUser(mode, formData.status, user.role_id)
  const bodegaYRecogeCompletos = bodegaRecogidaSeleccion !== '' && recogeRespuesta !== ''
  const canQuoteShipping = useMemo(
    () => mode === 'create' && canQuoteOrderShipment(formData, productDetails),
    [mode, formData, productDetails]
  )

  const runShippingQuote = useCallback(async (manualDimensions?: ManualPackageDimensions | null) => {
    const request = buildOrderQuoteRequest(formData, productDetails, manualDimensions)
    if (!request) return
    try {
      await cotizar(request.formData, request.orderDetail, request.tipoServicio)
    } catch (error) {
      console.error('Error al cotizar el envío del pedido:', error)
    }
  }, [formData, productDetails, cotizar])

  const handleQuoteShipping = useCallback(() => {
    if (hasCalculatedPackageDimensions(formData, productDetails)) {
      void runShippingQuote()
      return
    }
    setShowPackageDimensionsModal(true)
  }, [formData, productDetails, runShippingQuote])

  const handleConfirmPackageDimensions = useCallback((dims: ManualPackageDimensions) => {
    setShowPackageDimensionsModal(false)
    void runShippingQuote(dims)
  }, [runShippingQuote])

  const packageDimensionDefaults = useMemo(() => {
    const dims = packageDimensionsForOrderLines(formData.lineItems || [], productDetails)
    const text = (value: number) => value > 0 ? String(value) : ''
    return {
      largo: text(Number(dims.largo) || 0),
      ancho: text(Number(dims.ancho) || 0),
      altura: text(Number(dims.altura) || 0),
      peso: text(Number(dims.peso) || 0)
    }
  }, [formData.lineItems, productDetails])

  const handleSelectShippingQuote = useCallback((cotizacion: { carrier: string; precio: number }) => {
    const price = String(Math.round(cotizacion.precio))
    setCostoEnvio(price)
    handleInputChange('extraDetails', 'costo_envio', price)
    const carrier = mapQuoteCarrierToTransportadora(cotizacion.carrier)
    if (carrier) {
      handleInputChange('extraDetails', 'transportadora', carrier)
    }
    cerrarModal()
  }, [handleInputChange, cerrarModal])

  return (
    <>
      <form onSubmit={handleSubmit} className="bg-gray-50">
        {/* Botones de acción flotantes */}
        <OrderActions
          formData={formData}
          mode={mode}
          fieldsLocked={isFormRestricted}
          showNoteModal={modals.note}
          setShowNoteModal={setShowNoteModal}
          showAuditModal={modals.audit}
          setShowAuditModal={setShowAuditModal}
          showTrackingModal={modals.tracking}
          setShowTrackingModal={setShowTrackingModal}
          showShippingLabelModal={modals.shippingLabel}
          setShowShippingLabelModal={setShowShippingLabelModal}
          showInvoiceUploadModal={modals.invoiceUpload}
          setShowInvoiceUploadModal={setShowInvoiceUploadModal}
          onNoteChange={handleNoteChange}
            />
    
      {/* Información General */}
        <OrderGeneralInfo
          formData={formData}
            mode={mode}
            errors={errors}
          fieldsLocked={isFormRestricted}
          recogeEnBodega={recogeEnBodega}
          userRoleId={user.role_id}
          onInputChange={handleInputChange}
        />

      {/* Información del Cliente */}
        <CustomerInfoSection
          firstName={formData.customer?.first_name || ''}
          lastName={formData.customer?.last_name || ''}
          email={formData.customer?.email || ''}
          phone={formData.shippingAddress?.phone || formData.billingAddress?.phone || ''}
          tipoDocumento={formData.customer?.tipo_documento || ''}
          documento={formData.customer?.document || ''}
          onFirstNameChange={handleCustomerFirstNameChange}
          onLastNameChange={handleCustomerLastNameChange}
          onEmailChange={handleCustomerEmailChange}
          onPhoneChange={handleCustomerPhoneChange}
          onTipoDocumentoChange={handleCustomerTipoDocumentoChange}
          onDocumentoChange={handleCustomerDocumentoChange}
          mode={mode}
          tiposDeDocumento={tiposDoc}
          errors={errors}
          clienteEditable={!isFormRestricted}
          onEmailValidation={handleEmailValidation}
          firstNameSource="customer"
          documentoField="document"
        />

      {/* Bodega + fecha de entrega */}
        <OrderBodegaEntregaSection
          mode={mode}
          fieldsLocked={isFormRestricted}
          bodegaRecogidaSeleccion={bodegaRecogidaSeleccion}
          onBodegaRecogidaSeleccionChange={(value) => {
            handleBodegaRecogidaSeleccionChange(value)
            setErrors((prev) => ({ ...prev, bodega_recogida: '', recoge_respuesta: '' }))
          }}
          bodegaRecogidaError={errors.bodega_recogida}
          recogeRespuesta={recogeRespuesta}
          onRecogeRespuestaChange={(value) => {
            handleRecogeRespuestaChange(value)
            setErrors((prev) => ({ ...prev, recoge_respuesta: '' }))
          }}
          recogeError={errors.recoge_respuesta}
          fechaEntregaActiva={fechaEntregaActiva}
          onFechaEntregaActivaChange={(checked) => {
            handleFechaEntregaActivaChange(checked)
            setErrors((prev) => ({ ...prev, fecha_entrega: '' }))
          }}
          fechaEntrega={fechaEntrega}
          onFechaEntregaChange={(value) => {
            handleFechaEntregaChange(value)
            setErrors((prev) => ({ ...prev, fecha_entrega: '' }))
          }}
          fechaEntregaError={errors.fecha_entrega}
        />

      {/* Direcciones */}
        <AddressSection
          billingAddress={formData.billingAddress || {}}
          shippingAddress={formData.shippingAddress || {}}
          onBillingAddressChange={handleBillingAddressChange}
          onShippingAddressChange={handleShippingAddressChange}
          mode={mode}
          fieldsLocked={isFormRestricted}
          departamentos={deptos}
          ciudades={cities}
          errors={errors}
          renderDepartamentoOptions={renderDepartamentoOptions}
          renderCiudadOptions={renderCiudadOptions}
          recogeEnBodega={recogeEnBodega}
        />

        {/* Información de Envío */}
        <OrderShippingInfo
          formData={formData}
          mode={mode}
          fieldsLocked={isFormRestricted}
          costoEnvio={costoEnvio}
          onCostoEnvioChange={setCostoEnvio}
          onInputChange={handleInputChange}
          onQuoteShipping={mode === 'create' ? handleQuoteShipping : undefined}
          quoteShippingDisabled={!canQuoteShipping}
          quoteShippingLoading={cotizaciones.loading}
        />

      {/* Productos del Pedido */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <SectionTitle icon={CubeIcon} noMargin>Productos del Pedido</SectionTitle>
          {mode !== 'view' && (
            <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
              <Switch
                checked={isClienteNetoActive(formData.extraDetails)}
                onChange={handleClienteNetoChange}
                disabled={!canEditProducts}
                label="Cliente Neto"
              />
              <Switch
                checked={!!formData.extraDetails?.retencion_fuente_activa}
                onChange={(checked) => {
                  handleInputChange('extraDetails', 'retencion_fuente_activa', checked)
                  if (!checked) {
                    handleInputChange('extraDetails', 'retencion_fuente', '')
                  }
                }}
                disabled={!canEditProducts}
                label="Retención"
              />
              {canEditProducts && (
                <ProductSearchInput
                  searchTerm={searchTerm}
                  searchResults={searchResults}
                  isSearching={isSearching}
                  showResults={showResults && bodegaYRecogeCompletos}
                  onSearchChange={handleSearchChange}
                  onSelectProduct={handleSelectProduct}
                  onFocus={() => setShowResults(true)}
                  onBlur={() => setTimeout(() => setShowResults(false), 200)}
                  disabled={!bodegaYRecogeCompletos}
                  placeholder={
                    bodegaYRecogeCompletos
                      ? 'Buscar producto por nombre o SKU'
                      : 'Seleccione bodega y si recoge en bodega'
                  }
                />
              )}
            </div>
          )}
        </div>

        <OrderItemsTablePedido
          lineItems={formData.lineItems || []}
          productDetails={productDetails}
          loadingDetails={loadingDetails}
          expandedCombos={expandedCombos}
          mode={mode}
          fieldsLocked={!canEditProducts}
          formDataStatus={formData.status}
          onQuantityChange={handleQuantityChange}
          onRemoveProduct={handleRemoveProduct}
          onDiscountChange={handleDiscountChange}
          onPriceChange={handlePriceChange}
          onToggleCombo={toggleCombo}
          calculateTotals={calculateTotals}
          shippingCost={costoEnvio}
          allowPriceEdit={canEditProducts}
          retencionFuente={formData.extraDetails?.retencion_fuente ?? ''}
          onRetencionFuenteChange={(value) => handleInputChange('extraDetails', 'retencion_fuente', value)}
          retencionFuenteActiva={!!formData.extraDetails?.retencion_fuente_activa}
          clienteNeto={isClienteNetoActive(formData.extraDetails)}
          recogeEnBodega={usaSaldoBodegaSeleccionada}
          bodegaRecogidaSeleccion={bodegaRecogidaSeleccion}
        />
      </div>

      {/* Información de Pago */}
        <OrderPaymentInfo
          formData={formData}
          mode={mode}
          fieldsLocked={isFormRestricted}
          errors={errors}
          formasPago={formasPago}
          metodosPago={metodosPago}
          plazosPago={plazosPago}
          pagosParciales={pagosParciales}
          esValidoPagosParciales={esValidoPagosParciales}
          totalPedidoPagosParciales={totalPedidoPagosParciales}
          sumaPagosParciales={sumaPagosParciales}
          onInputChange={handleInputChange}
          onAgregarPagoParcial={agregarPagoParcial}
          onEliminarPagoParcial={eliminarPagoParcial}
          onActualizarPagoParcial={actualizarPagoParcial}
          orderId={mode === 'edit' ? initialData?.id : undefined}
          getOrderTotalCop={getOrderTotalCop}
        />

        {/* Botones de acción principales */}
        {mode === 'create' && (
          <div className="mt-6 mb-6 flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 sticky bottom-0 bg-gray-50 py-4 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={() => window.history.back()}
              leftIcon={XMarkIcon}
              fullWidth
              className="sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="success"
              disabled={
                !isStockAvailable() ||
                !isPagosParcialesValid() ||
                !isBoldPaymentAmountValid() ||
                isSubmitting ||
                !bodegaYRecogeCompletos
              }
              isLoading={isSubmitting}
              leftIcon={CheckIcon}
              fullWidth
              className="sm:w-auto"
            >
              {isSubmitting ? 'Creando...' : 'Crear Pedido'}
            </Button>
          </div>
        )}
        {mode === 'edit' && (
          <div className="mt-6 mb-6 flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 sticky bottom-0 bg-gray-50 py-4 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={() => window.history.back()}
              leftIcon={XMarkIcon}
              fullWidth
              className="sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={
                !isStockAvailable() ||
                isSubmitting ||
                !bodegaYRecogeCompletos
              }
              isLoading={isSubmitting}
              leftIcon={CheckIcon}
              fullWidth
              className="sm:w-auto"
            >
              {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        )}
    </form>
      {mode === 'create' && (
        <>
          <OrderQuoteDimensionsModal
            isOpen={showPackageDimensionsModal}
            initial={packageDimensionDefaults}
            onClose={() => setShowPackageDimensionsModal(false)}
            onConfirm={handleConfirmPackageDimensions}
          />
          <CotizacionesModal
            isOpen={showCotizacionesModal}
            onClose={cerrarModal}
            cotizaciones={cotizaciones}
            todasLasCotizaciones={todasLasCotizaciones}
            onSelectCotizacion={handleSelectShippingQuote}
            loading={cotizaciones.loading}
          />
        </>
      )}
    </>
  )
}

export default OrderForm
